import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as authApi from '../lib/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

export function AuthProvider({ children }) {
  // null = checking, object = signed in, false = signed out
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const u = await authApi.fetchMe();
      setUser(u || false);
      return u;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // If we're returning from an OAuth callback, AuthCallback handles it.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      return;
    }
    refresh();
  }, [refresh]);

  const value = {
    user,
    loading: user === null,
    signedIn: !!user,
    isAdmin: !!(user && user.is_admin),
    isPro: !!(user && user.plan === 'pro'),
    aiCredits: (user && user.ai_credits) || 0,
    refresh,
    login: async (creds) => {
      const u = await authApi.login(creds);
      setUser(u);
      return u;
    },
    register: async (creds) => {
      const u = await authApi.register(creds);
      setUser(u);
      return u;
    },
    completeGoogleLogin: async (sessionId) => {
      const u = await authApi.exchangeGoogleSession(sessionId);
      setUser(u);
      return u;
    },
    logout: async () => {
      await authApi.logout();
      setUser(false);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
